"use client";

import { useI18n } from "@/i18n";
import { ActionButton, Dots, Input } from "@repo/uikit";
import { ArrowRight } from "lucide-react";
import { TurnstileWidget } from "./turnstile-widget";

interface EmailStepProps {
  email: string;
  setEmail: (email: string) => void;
  isSending: boolean;
  canSubmitEmail: boolean;
  emailError?: string;
  turnstileSiteKey: string;
  turnstileResetKey: number;
  onTurnstileToken: (token: string) => void;
  onSubmit: () => void;
}

export default function EmailStep({
  email,
  setEmail,
  isSending,
  canSubmitEmail,
  emailError = "",
  turnstileSiteKey,
  turnstileResetKey,
  onTurnstileToken,
  onSubmit,
}: EmailStepProps) {
  const { t } = useI18n();

  return (
    <>
      <div className="">
        <Input
          type="email"
          placeholder={t("auth.emailPlaceholder")}
          aria-label={t("auth.emailLabel")}
          data-testid="login-email-input"
          data-i18n="auth.emailPlaceholder"
          autoFocus
          aria-invalid={emailError ? true : undefined}
          className={emailError ? "border-destructive focus-visible:border-destructive" : ""}
          value={email}
          onChange={(event: any) => setEmail(event.target.value)}
          onKeyDown={async (event) => {
            if (event.key === "Enter" && canSubmitEmail) {
              await onSubmit();
            }
          }}
        />
      </div>

      {emailError ? (
        <p className="mt-2 text-xs font-medium text-destructive" data-testid="login-email-error">
          {emailError}
        </p>
      ) : null}

      <div className="mt-2">
        <p className="text-xs italic text-muted-foreground" data-i18n="auth.confirmationNote">
          {t("auth.confirmationNote")}
        </p>
        <p className="mt-2 text-xs italic text-muted-foreground" data-i18n="auth.autoAccountNote">
          {t("auth.autoAccountNote")}
        </p>
      </div>

      <TurnstileWidget siteKey={turnstileSiteKey} resetKey={turnstileResetKey} onToken={onTurnstileToken} />

      <div className="mt-5">
        <ActionButton
          data-testid="login-email-continue-btn"
          data-i18n="common.buttons.continue"
          loading={isSending}
          disabled={!canSubmitEmail}
          className="w-full"
          onClick={onSubmit}
        >
          {isSending ? (
            <Dots data-i18n="auth.sending">{t("auth.sending")}</Dots>
          ) : (
            <>
              <span data-i18n="common.buttons.continue">{t("common.buttons.continue")}</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </ActionButton>
      </div>
    </>
  );
}
